import { FC, memo, useEffect, useRef } from 'react';

import { FiChevronRight } from 'react-icons/fi';
import { useForm } from 'react-hook-form';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { usePasswordRecovery } from '~/modules/auth/hooks/usePasswordRecovery';

import { FormGroup } from '~/shared/components/Form/FormGroup';
import { CustomRadio } from '~/shared/components/Form/CustomRadio';
import { ControlledOTPInput } from '~/shared/components/Form/ControlledOTPInput';

import { IPasswordRecoveryFormData } from '~/modules/auth/interfaces/IPasswordRecoveryFormData';

import { StepWrapper } from '../StepWrapper';

const formSchemaValidation = Yup.object().shape({
  code: Yup.string()
    .required('Código de recuperação é obrigatório')
    .min(6, 'Código de recuperação inválido'),
});

const BaseStepThree: FC = () => {
  const {
    validateRecoveryCode,
    recoveryCodeIsValidating,
    validateRecoveryCodeError,
    selectedMethodOption,
    changeMethod,
    currentStep,
  } = usePasswordRecovery();

  const isValidatingAtFirstTime = useRef(true);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    watch,
  } = useForm<Pick<IPasswordRecoveryFormData, 'code'>>({
    resolver: yupResolver(formSchemaValidation),
    defaultValues: { code: '' },
  });

  const code = watch?.()?.code;

  useEffect(() => {
    if (currentStep < 3) {
      isValidatingAtFirstTime.current = true;
      setValue('code', '');
      setError('code', { message: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (isValidatingAtFirstTime.current && code?.length === 6) {
      isValidatingAtFirstTime.current = false;
      validateRecoveryCode({ code });
    }
  }, [code, validateRecoveryCode]);

  useEffect(() => {
    if (typeof validateRecoveryCodeError !== 'undefined') {
      setError('code', { message: validateRecoveryCodeError });
    }
  }, [validateRecoveryCodeError, setError]);

  return (
    <StepWrapper
      step={3}
      onSubmit={handleSubmit(validateRecoveryCode)}
      isSubmitting={recoveryCodeIsValidating}
    >
      <FormGroup>
        <FormGroup>
          <CustomRadio
            {...selectedMethodOption}
            description="Clique para alterar a forma"
            customEndIcon={FiChevronRight}
            onClick={changeMethod}
          />
        </FormGroup>

        <ControlledOTPInput
          control={control}
          name="code"
          size="lg"
          error={errors.code}
          disabled={recoveryCodeIsValidating}
        />
      </FormGroup>
    </StepWrapper>
  );
};

export const StepThree = memo(BaseStepThree);
